import React from "react";

// Components
import { InputNumber, Select, Tooltip } from "antd";

import useProductCreateFlow from "hooks/reduxContainer/fulfillment/productCreateFlow";

import colors from "css/colors";

import Input from "../components/Input";
import { Label, RedText, Text } from "../components/Input/Input.styles";
import { HeaderText } from "../Header/Header.styles";
import { Wrapper, Column } from "../ProductInfo/ProductInfo.styles";

// Constants

// Hooks

import {
  SectionCard,
  Section,
  LineBreak,
  Row,
  HSCodeLookupLink,
  LinkWrapper,
  CostInputColumn,
  QuestionMark,
} from "./CustomsInfo.styles";
import { useQuery } from "react-query";
import { fetchCountryIndex } from "../../../../api/common/country";

interface Props {
  hs_tariff_code: string;
  customs_description: string | null;
  cost: number;
  wholesale_price: number;
  retail_price: number;
  manufacturingCountry?: string;
}

const CustomsInfo: React.FC<Props> = ({
  hs_tariff_code,
  customs_description,
  cost,
  wholesale_price,
  retail_price,
  manufacturingCountry,
}) => {
  const {
    isEditing,
    dispatchSetTariffCode,
    dispatchSetCustomsDescription,
    dispatchSetCost,
    dispatchSetWholesalePrice,
    dispatchSetRetailPrice,
    dispatchSetManufacturingCountry,
  } = useProductCreateFlow();

  const countryIndex = useQuery(["countries"], fetchCountryIndex, {
    enabled: isEditing,
    select: (countries) => countries.flatMap((c) => ({ label: c.name.common, value: c.name.common })),
  });

  const renderRetailTooltip = () => (
    <Tooltip title="This value will be used to declare the product's value for international shipments">
      <QuestionMark />
    </Tooltip>
  );

  const renderCostTooltip = () => (
    <Tooltip title="This value will be used to determine the product's default insurance value">
      <QuestionMark />
    </Tooltip>
  );

  const renderCostInputColumn = (
    label: string,
    required: boolean,
    handleChange: (value: number) => void,
    value: number
  ) => (
    <CostInputColumn>
      <Label>
        {label}
        {required ? <RedText>*</RedText> : null}
        {label === "Retail" ? renderRetailTooltip() : null}
        {label === "Cost" ? renderCostTooltip() : null}
      </Label>
      {isEditing ? (
        <InputNumber
          type="currency"
          disabled={!isEditing}
          onFocus={(e) => e.target.select()}
          formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
          parser={(value) => (value?.replace(/\$\s?|(,*)/g, "") as any) || ""}
          onChange={(value) => {
            handleChange(Number(value));
          }}
          defaultValue={value}
        />
      ) : (
        <Text>${value}</Text>
      )}
    </CostInputColumn>
  );

  const getDefaultDescriptionValue = () => {
    if (customs_description) return customs_description;
    else if (isEditing) return undefined;
    else return "No Description";
  };

  const convertDollarToCent = (value: number) => {
    return value * 100;
  };

  const convertCentToDollar = (value: number) => {
    return value / 100;
  };

  return (
    <Wrapper>
      <HeaderText margin="0 0 16px">CUSTOMS INFORMATION</HeaderText>
      <SectionCard>
        <Section flex={1}>
          <Column flex={1}>
            <Row>
              <Input
                disabled={!isEditing}
                label="Tariff Code"
                defaultValue={hs_tariff_code}
                handleChange={(value) => dispatchSetTariffCode(value)}
                displayOnly={!isEditing}
              />
            </Row>
            {isEditing ? (
              <LinkWrapper>
                <HSCodeLookupLink
                  target="_blank"
                  href="https://www.canadapost.ca/cpotools/apps/wtz/business/findHsCode"
                >
                  HS Code Lookup
                </HSCodeLookupLink>
              </LinkWrapper>
            ) : null}
          </Column>
        </Section>
        <LineBreak />
        <Section flex={3}>
          <Column flex={1}>
            <Row margin="0 0 24px">
              {renderCostInputColumn(
                "Retail",
                true,
                (value) => dispatchSetRetailPrice(Number(convertDollarToCent(value) || 0)),
                convertCentToDollar(retail_price)
              )}
              {renderCostInputColumn(
                "Wholesale",
                false,
                (value) => dispatchSetWholesalePrice(Number(convertDollarToCent(value) || 0)),
                convertCentToDollar(wholesale_price)
              )}
              {renderCostInputColumn(
                "Cost",
                false,
                (value) => dispatchSetCost(Number(convertDollarToCent(value) || 0)),
                convertCentToDollar(cost)
              )}

              <CostInputColumn>
                <Label>Manufacturing Country of Origin</Label>
                {isEditing ? (
                  <Select
                    showSearch
                    placeholder="Select a country"
                    optionFilterProp="children"
                    loading={countryIndex.isLoading}
                    onChange={dispatchSetManufacturingCountry}
                    defaultValue={manufacturingCountry}
                    filterOption={(input, option) =>
                      String(option?.label || "")
                        .toLocaleLowerCase()
                        .includes(input.toLocaleLowerCase())
                    }
                    options={countryIndex.data}
                  />
                ) : (
                  <Text>{manufacturingCountry || "N/A"}</Text>
                )}
              </CostInputColumn>
            </Row>
            <Row>
              <Input
                textarea
                label="Description"
                placeholder="Add description here..."
                disabled={!isEditing}
                defaultValue={getDefaultDescriptionValue()}
                handleChange={(value) => dispatchSetCustomsDescription(value)}
                displayOnly={!isEditing}
                color={getDefaultDescriptionValue() ? colors.CHARCOAL : colors.GREY}
                fontWeight={
                  getDefaultDescriptionValue() !== "undefined" && getDefaultDescriptionValue() !== "No Description"
                    ? 600
                    : 500
                }
              />
            </Row>
          </Column>
        </Section>
      </SectionCard>
    </Wrapper>
  );
};

export default CustomsInfo;
